/**
 * Onboarding-wizard funnel analytics: one RUN per wizard open, events posted
 * to `POST /api/telemetry/onboarding` (contract:
 * `@shared/modules/telemetry/onboardingContract`), aggregated by the admin
 * Insights "Onboarding funnel" card.
 *
 * Fire-and-forget like the impression beacon (`hooks/useImpression.ts`):
 * `keepalive` so a close-then-navigate or tab close still delivers, every
 * failure swallowed, and a no-op without a principal (no CSRF token = no
 * customer/anon session to attribute the run to). Never sends PII — only the
 * contract's enums, the run id and step positions.
 */
import { apiCall } from "./api";
import type {
  OnboardingEntry,
  OnboardingEvent,
  OnboardingEventBody
} from "@shared/modules/telemetry/onboardingContract";

export type { OnboardingEntry };

export type OnboardingRun = {
  readonly runId: string;
  readonly entry: OnboardingEntry;
  /** Read at send time — the AuthProvider may refresh the token mid-run. */
  readonly getCsrfToken: () => string | null;
};

export type OnboardingEventProps = Omit<OnboardingEventBody, "runId" | "event" | "entry">;

function newRunId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `run-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function startOnboardingRun(
  entry: OnboardingEntry,
  getCsrfToken: () => string | null
): OnboardingRun {
  return { runId: newRunId(), entry, getCsrfToken };
}

export function track(run: OnboardingRun, event: OnboardingEvent, props: OnboardingEventProps = {}): void {
  const csrfToken = run.getCsrfToken();
  if (!csrfToken) return;
  const body: OnboardingEventBody = { ...props, runId: run.runId, event, entry: run.entry };
  void apiCall("/api/telemetry/onboarding", {
    method: "POST",
    body,
    csrfToken,
    keepalive: true
  }).catch(() => {
    // analytics must never surface in the UX
  });
}
